import React, { FC, useEffect, useState } from 'react';

// Components
import { RefundForm } from '../../components/forms';
import { CircleLoader } from '../../components/common';

// Models
import { RefundFormValue } from '../../models/Forms/RefundFormValue';
import { PageView } from '../../models/PageView';
import { RefundFormPageContent } from '../../models/Responses/RefundFormPageContent';

// Hooks
import { usePrevious } from '../../hooks/usePrevious';

// i18n
import { useTranslation } from '../../translations/useTranslation';

// Context
import { useGlobalState } from '../../context/global-context/hooks';

// Utils
import { scrollToTop } from '../../utilities/utils';

// Services
import {
  createRefundCase,
  getRefundFormPageData,
} from '../../services/formService';

const initialValues: RefundFormValue = {
  amount: '' as unknown as number, // Want to set input field as empty with enabling this type as string
  months: 2,
  suggestions: '',
  name: '',
  email: '',
  phone: '',
  reference: '',
  honeyPot: '', // Hidden field to prevent spam bots
};

const RefundFormPage: FC = () => {
  const [view, setView] = useState<PageView>('loading');
  const { prev: prevView } = usePrevious<PageView>(view);
  const [formState, setFormState] = useState<RefundFormValue>(initialValues);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [pageContent, setPageContent] = useState<RefundFormPageContent>();

  const { t, keys } = useTranslation();
  const { state } = useGlobalState();

  useEffect(() => {
    // Scroll to top when view changes
    if (!prevView || prevView === 'loading') return;
    scrollToTop();
  }, [view]);

  useEffect(() => {
    getRefundFormPageData(state.pageId).then((response) => {
      setView('form');
      setPageContent(response.data);
    });
  }, []);

  const handleSubmit = async (values: RefundFormValue) => {
    setFormState(values);
    try {
      const response = await createRefundCase({
        amount: values.amount,
        months: values.months === 0 ? undefined : values.months,
        suggestions: values.suggestions,
        name: values.name,
        email: values.email,
        phone: values.phone,
        reference: values.reference,
        umbracoPageId: state.pageId,
        honeyPot: values.honeyPot,
      });
      if (response.data.ThankYouPageUrl) {
        setIsRedirecting(true);
        location.href = response.data.ThankYouPageUrl;
      } else {
        resetForm();
        resetView();
      }
    } catch (err) {
      // TODO: Custom error messages depending on status code?
      setView('error');
    }
  };

  const resetView = () => {
    setView('form');
  };

  const resetForm = () => {
    setFormState(initialValues);
  };

  return (
    <div className="form-app__refund-form">
      {view === 'form' && (
        <RefundForm
          formValues={formState}
          isRedirecting={isRedirecting}
          introductionText={pageContent?.IntroductionText}
          onSubmit={handleSubmit}
        />
      )}
      {view === 'loading' && <CircleLoader size="sm" />}
      {view === 'error' && (
        <div>
          <h2>{t(keys.uiErrorTitle)}...</h2>
          <p>{t(keys.resumedMemberShipFormErrorMessage)}</p>
          <button
            className="button form-app__button form-app__button--auto-width"
            onClick={resetView}
          >
            {t(keys.uiTryAgain)}
          </button>
        </div>
      )}
    </div>
  );
};

export default RefundFormPage;
